import { useEffect, useState } from "react";
import {
  searchTags,
  fetchRecommendedTags,
  transformRecommendTagsResponse,
  tagManager,
} from "@/shared/api/newsquawk-headlines";
import { rankTagHits, type RankedTag } from "@/shared/utils/tagSearch";
import { filterTagRecommendQuery } from "@/utils/text-utils";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";

/** Below this a query matches most of the taxonomy and answers nothing. */
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 25;

export interface UseTagLibrarySearchResult {
  tags: RankedTag[];
  isLoading: boolean;
  /** A query ran and the library had nothing — distinct from "not asked yet". */
  isEmpty: boolean;
}

/**
 * Searches the whole tag library for a typed query.
 *
 * The panels this backs list only what the user already owns plus the ~51
 * curated menu tags, which is a rounding error against the taxonomy's tens of
 * thousands. This is the rest of it, on demand: `/tags/search` for the typed
 * term, re-ranked because the endpoint returns id-ordered substring hits with
 * no relevance score, falling back to `/tags/recommend` when the substring
 * search finds nothing at all — the same pairing `resolveTerms` uses, and the
 * only way a query phrased as words rather than a label ever resolves.
 *
 * The taxonomy is never fetched wholesale: `/tags/simple` would make this
 * trivial and put a curated corpus of tens of thousands of tags in the browser.
 * Results are pushed into the shared `tagManager` cache so a host can name a
 * tag the user picked without asking for it again.
 */
export function useTagLibrarySearch(query: string): UseTagLibrarySearchResult {
  const debounced = useDebouncedValue(query.trim(), DEBOUNCE_MS);
  const [tags, setTags] = useState<RankedTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (debounced.length < MIN_QUERY_LENGTH) {
      setTags([]);
      setIsLoading(false);
      setSearched(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setIsLoading(true);

    (async () => {
      let ranked: RankedTag[] = [];
      try {
        // Searched raw. `filterTagRecommendQuery` drops every word of three
        // characters or fewer, which is written for the recommender's long
        // text and would silently swallow "ecb", "fed" and "oil" here.
        const hits = (await searchTags(debounced, controller.signal)) ?? [];
        ranked = rankTagHits(debounced, hits);

        if (ranked.length === 0) {
          // Words rather than a label ("central bank rate decisions") match no
          // substring, but the recommender reads them fine — and it is the one
          // endpoint that wants the sanitiser.
          const forRecommend = filterTagRecommendQuery(debounced);
          if (forRecommend) {
            const recommended = await fetchRecommendedTags(
              forRecommend,
              controller.signal
            );
            ranked = transformRecommendTagsResponse(recommended, 0.3).map((t) => ({
              ...t,
              matchedLabel: t.name,
              strength: "weak" as const,
              category: recommended.tags?.[String(t.tag_id)]?.category,
            }));
          }
        }
      } catch {
        // Non-fatal, and deliberately quiet: the panel still lists everything
        // the user owns, so a lookup failure costs discovery, not the filter.
        ranked = [];
      }

      if (cancelled) return;
      tagManager.addToCache(
        ranked.map(({ tag_id, name, score }) => ({ tag_id, name, score }))
      );
      setTags(ranked.slice(0, MAX_RESULTS));
      setSearched(true);
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debounced]);

  return { tags, isLoading, isEmpty: searched && !isLoading && tags.length === 0 };
}
